import React, { useMemo } from 'react';
import { alpha, Chip, Theme, useMediaQuery, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  PaymentsOutlined,
  EventOutlined,
  InfoOutlined,
  InsertChartOutlined,
  ReportProblemOutlined,
  ChevronRight,
  CheckCircle,
  AddCircleOutline,
  Warning,
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { WebshopShopDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { useRestaurantOrders, useRestaurantPreorders } from '../../apis/orders-api';
import { useRestaurantOpeningHours } from '../../apis/shops-api';
import { useEcomPaymentMethodSettings, useMerchant } from '../../apis/payments-api';
import { useConciseProductMenu } from '../../apis/products-api';
import LoadingIndicator from '../../shared/LoadingIndicator';
import { DeliveryIcon } from '../../shared/Icons/DeliveryIcon';
import { PickupIcon } from '../../shared/Icons/PickupIcon';
import { OutOfStockItem, useCurrentShopOutOfStockItems } from '../RestaurantsPage/hooks/useRestaurantData';
import { FinnishDayAndMonthFormat, FinnishShortTimeFormat } from '../../shared/utils/dateHelpers';
import { isForbiddenError } from '../../shared/utils/apiErrors';
import { ForbiddenNote } from '../../shared/PermissionNote/ForbiddenNote';
import { RestaurantPageHeader } from '../../shared/RestaurantPageHeader/RestaurantPageHeader';
import {
  ChannelInfo,
  countProductsInOrder,
  countProductsInOrders,
  DELIVERY_OPENING_HOUR_TYPE_ID,
  getChannelInfo,
  getPaymentMethodRows,
  getEatInChannelInfo,
  getHourlyOrderCounts,
  getPreorderWeek,
  getTodaysOpenHourWindow,
  getUpcomingPreorders,
  HourlyOrders,
  HoursSource,
  PaymentMethodRow,
  PreorderDay,
  isSlowEstimate,
  PICKUP_OPENING_HOUR_TYPE_ID,
  StatusTone,
  TODAY_REFETCH_INTERVAL_MS,
  TodaysHours,
  UpcomingPreorder,
} from './utils';
import { CHART_COLORS } from '../../design-system/tokens/chartPalette';
import { Card, PillDetail, PillIcon, StatLabel, StatusPill, StatValue, toneColor } from './styles';

/* ---------------------------------- shells --------------------------------- */

export const DashboardCard: React.FC<
  React.PropsWithChildren<{ title?: string; icon?: React.ReactNode; to?: string }>
> = ({ title, icon, to, children }) => (
  <Card>
    {title && (
      <CardHeader>
        <CardTitle>
          {icon}
          <span>{title}</span>
        </CardTitle>

        {to && (
          <CardLink to={to} aria-label={title}>
            <ChevronRight fontSize="small" />
          </CardLink>
        )}
      </CardHeader>
    )}
    {children}
  </Card>
);

/* ------------------------------- status header ----------------------------- */

// Webshop hours get their own wording rather than the plain "Avoinna …": they are a fallback for
// shops that never filled in the premises' hours, and stating them as if they were the restaurant's
// would have the header quietly answer a different question than the one it looks like it answers.
const HOURS_CHIP_KEYS: Record<HoursSource, Record<TodaysHours['kind'], string>> = {
  restaurant: {
    closed: 'singleRestaurant.hours.closedToday',
    allDay: 'singleRestaurant.hours.allDay',
    hours: 'singleRestaurant.hours.openToday',
  },
  webshop: {
    closed: 'singleRestaurant.hours.webshopClosed',
    allDay: 'singleRestaurant.hours.webshopAllDay',
    hours: 'singleRestaurant.hours.webshopToday',
  },
};

const hoursChipKey = (todaysHours: TodaysHours): string => HOURS_CHIP_KEYS[todaysHours.source][todaysHours.kind];

export const StatusHeader: React.FC<{
  restaurant: WebshopShopDto;
  statusLabel: string;
  statusTone: StatusTone;
  statusIcon: React.ReactNode;
  statusDetail?: string;
  // The premises' own hours for today. Undefined while the hours load, and for a shop that has none
  // configured — both of which show no chip at all rather than a claim about the day.
  todaysHours?: TodaysHours;
  // The overview of all the user's restaurants, for someone who has several; a one-shop user has
  // nowhere above this page to go back to.
  backTo?: string;
}> = ({ restaurant, statusLabel, statusTone, statusIcon, statusDetail, todaysHours, backTo }) => {
  const { t } = useTranslation();
  return (
    <RestaurantPageHeader
      title={t('nav.restaurant.start')}
      restaurant={restaurant}
      backTo={backTo}
      backLabel={t('nav.restaurant.all')}
      actions={
        <HeaderRight>
          <StatusPill $tone={statusTone}>
            <PillIcon>{statusIcon}</PillIcon>
            <span>{statusLabel}</span>
            {statusDetail && <PillDetail>{statusDetail}</PillDetail>}
          </StatusPill>

          {todaysHours && (
            <HoursChip
              size="small"
              color="default"
              variant="outlined"
              label={t(hoursChipKey(todaysHours), {
                hours: todaysHours.kind === 'hours' ? todaysHours.hours : undefined,
              })}
            />
          )}
        </HeaderRight>
      }
    />
  );
};

/* --------------------------------- channels -------------------------------- */

const estimateTone = (info: ChannelInfo): StatusTone => {
  if (info.state === 'closed') return 'error';
  if (info.state === 'restricted' || isSlowEstimate(info.estimate ?? 0)) return 'warning';
  return 'open';
};

// One column of the channels card. `info` is undefined while the channel's hours are still
// loading, which renders a neutral placeholder instead of a state it cannot vouch for.
const ChannelColumn: React.FC<{ label: string; icon: React.ReactNode; info?: ChannelInfo }> = ({
  label,
  icon,
  info,
}) => {
  const { t } = useTranslation();
  const tone = info ? estimateTone(info) : 'neutral';
  const isClosed = info?.state === 'closed';

  /**
   * Three lines, the middle one carrying the state in its own colour rather than a separate badge:
   * the ETA where there is one, a red "Kiinni" where the channel is shut, and a plain "Auki" for
   * eat-in, which never has an estimate. Dropping the badge is most of what made the card shorter,
   * and colour says the same thing in less room — amber for a slow or shortened day, red for closed.
   */
  const value = !info
    ? '–'
    : isClosed
    ? t('Closed')
    : info.estimate != null
    ? `${info.estimate} min`
    : t('singleRestaurant.status.open');

  return (
    <Channel>
      <ChannelLabel>
        <ChannelIcon>{icon}</ChannelIcon>
        <span>{label}</span>
      </ChannelLabel>
      <ChannelValue $tone={tone}>{value}</ChannelValue>
      {/* A closed channel's hours are noise — it is shut whatever they say. Amber on a restricted
          one points at the evidence: without it, an amber value alone would read the same whether
          the day is cut short or the ETA is merely slow, since both wear that colour. */}
      <ChannelHours $tone={info?.state === 'restricted' ? 'warning' : undefined}>
        {isClosed ? '' : info?.todaysHours ?? ''}
      </ChannelHours>
    </Channel>
  );
};

/**
 * Pickup and eat-in are one thing from the customer's side — come to the restaurant — running to the
 * same clock and the same wait, so they share a column rather than repeating each other.
 *
 * Pickup carries the numbers, since eat-in has no ETA of its own. Eat-in only stands in when pickup
 * itself is off, so a dining room that is open is never reported shut.
 */
const mergeCounterChannels = (pickup: ChannelInfo, eatIn?: ChannelInfo): ChannelInfo =>
  pickup.state !== 'closed' || !eatIn || eatIn.state === 'closed' ? pickup : eatIn;

// Presentational half of ChannelsCard, so the states can be rendered from fixtures (see testStates).
export const ChannelsCardView: React.FC<{ delivery: ChannelInfo; pickup: ChannelInfo; eatIn?: ChannelInfo }> = ({
  delivery,
  pickup,
  eatIn,
}) => {
  const { t } = useTranslation();

  return (
    <DashboardCard>
      <ChannelRow>
        <ChannelColumn
          label={t('singleRestaurant.deliveryType.delivery')}
          icon={<DeliveryIcon width={24} height={24} color="currentColor" />}
          info={delivery}
        />
        <ChannelColumn
          label={t('singleRestaurant.channels.pickupAndEatin')}
          icon={<PickupIcon width={24} height={24} color="currentColor" />}
          info={mergeCounterChannels(pickup, eatIn)}
        />
      </ChannelRow>
    </DashboardCard>
  );
};

// Delivery, pickup and eat-in side by side: one card wide enough to compare the three at a glance,
// replacing the separate delivery/pickup tiles that used to sit in the KPI row.
export const ChannelsCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const { data: openingHours, error: openingHoursError } = useRestaurantOpeningHours(restaurant.shopExternalId);

  // Delivery and pickup come off the shop itself, so they survive the hours query being refused —
  // only eat-in's hours would be missing, and the column already handles not knowing.
  if (isForbiddenError(openingHoursError) && !openingHours) {
    return (
      <DashboardCard>
        <ForbiddenNote />
      </DashboardCard>
    );
  }

  const delivery = getChannelInfo(
    restaurant,
    DELIVERY_OPENING_HOUR_TYPE_ID,
    restaurant.hasDelivery,
    restaurant.currentDeliveryEstimate
  );
  const pickup = getChannelInfo(
    restaurant,
    PICKUP_OPENING_HOUR_TYPE_ID,
    restaurant.hasPickup,
    restaurant.currentPickupEstimate
  );

  return <ChannelsCardView delivery={delivery} pickup={pickup} eatIn={getEatInChannelInfo(restaurant, openingHours)} />;
};

/* ---------------------------------- today ---------------------------------- */

export type TodayStats = { orderCount: number; productCount: number };

// "09", "18", and 24 wraps back to "00" for the end of the last slot of the day.
const formatHour = (hour: number): string => `${(hour % 24).toString().padStart(2, '0')}:00`;

// Presentational half of TodayCard, so the states can be rendered from fixtures (see testStates).
// `stats` is null while the day's orders are loading.
export const TodayCardView: React.FC<{
  stats: TodayStats | null;
  hourly: HourlyOrders;
  ordersPath?: string;
  // Epoch millis of the last successful fetch (react-query's dataUpdatedAt). Restarts the cycle bar;
  // undefined before the first one lands, which keeps the bar hidden.
  refreshedAt?: number;
  isFetching?: boolean;
}> = ({ stats, hourly, ordersPath, refreshedAt, isFetching }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Phones and small tablets: the chart trades axis chrome for bar width (see the tick formatters).
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  // The day's busiest hour, called out next to the caption so the single most useful number on the
  // chart is readable without hovering — which on a touch screen means a deliberate tap-and-hold.
  const peak = useMemo(
    () =>
      hourly.reduce<HourlyOrders[number] | undefined>(
        (best, bar) => (bar.value > (best?.value ?? 0) ? bar : best),
        undefined
      ),
    [hourly]
  );

  const tickLabelStyle = isNarrow ? { fontSize: 10 } : undefined;

  return (
    <DashboardCard
      title={t('singleRestaurant.today.title')}
      icon={<InsertChartOutlined fontSize="small" />}
      to={ordersPath}
    >
      {/* The track is always there so the card has a visible refresh affordance from first paint;
          the fill only starts once a fetch has actually landed, and is remounted on each one, which
          is what restarts the sweep from zero. */}
      <RefreshTrack title={t('singleRestaurant.today.autoRefresh')}>
        {refreshedAt != null && (
          <RefreshFill key={refreshedAt} $cycleMs={TODAY_REFETCH_INTERVAL_MS} $fetching={Boolean(isFetching)} />
        )}
      </RefreshTrack>

      <StatRow>
        <Stat>
          <StatLabel>{t('singleRestaurant.today.orders')}</StatLabel>
          <StatValue>{stats ? stats.orderCount : '–'}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>{t('singleRestaurant.today.products')}</StatLabel>
          <StatValue>{stats ? stats.productCount : '–'}</StatValue>
        </Stat>
      </StatRow>

      <CaptionRow>
        <ChartCaption>{t('singleRestaurant.today.ordersPerHour')}</ChartCaption>
        {peak && peak.value > 0 && (
          <PeakNote>
            {t('singleRestaurant.today.peak', {
              hour: formatHour(peak.hour),
              value: `${peak.value} ${t('singleRestaurant.orders', { count: peak.value })}`,
            })}
          </PeakNote>
        )}
      </CaptionRow>
      {hourly.length === 0 ? (
        <EmptyText>{t('singleRestaurant.today.noOrders')}</EmptyText>
      ) : (
        <ChartBox>
          <BarChart
            height={200}
            margin={{ top: 8, right: isNarrow ? 2 : 8, bottom: 4, left: 0 }}
            series={[
              {
                data: hourly.map((bar) => bar.value),
                // Labelled so the hover tooltip names what the figure counts, even though the
                // legend stays hidden — with one series the caption above already says it.
                label: t('singleRestaurant.today.orders'),
                color: CHART_COLORS.brandGreen,
                valueFormatter: (value) => `${value ?? 0}`,
              },
            ]}
            hideLegend
            xAxis={[
              {
                data: hourly.map((bar) => bar.hour),
                scaleType: 'band',
                // Bare hour number on the ticks ("18"), the full slot in the tooltip heading
                // ("18:00 – 19:00"). Two characters per tick is what lets 'auto' keep most hour
                // labels on a phone instead of dropping every other one.
                valueFormatter: (hour: number, context) =>
                  context.location === 'tick'
                    ? hour.toString().padStart(2, '0')
                    : `${formatHour(hour)} – ${formatHour(hour + 1)}`,
                tickLabelStyle,
                // Narrower category padding gives the bars themselves the reclaimed pixels.
                categoryGapRatio: isNarrow ? 0.12 : 0.2,
              },
            ]}
            yAxis={[
              {
                // Hourly order counts are two digits at most, so the gutter stays narrow on every screen.
                width: 34,
                // Whole orders only: a quiet day would otherwise get ticks at 0.5, 1.5…
                tickMinStep: 1,
                tickLabelStyle,
                // On a phone the horizontal grid lines already mark the levels, so the tick marks
                // themselves are 6px of pure overhead, and four gridlines read more cleanly than six.
                disableTicks: isNarrow,
                tickNumber: isNarrow ? 4 : undefined,
              },
            ]}
            borderRadius={4}
            grid={{ horizontal: true }}
          />
        </ChartBox>
      )}
    </DashboardCard>
  );
};

// Today at a glance: order count and product count over one shared set of orders, with the hourly
// order bars underneath — replaces the separate sales/orders KPI tiles.
export const TodayCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const { t } = useTranslation();
  // Self-refreshing, on the same terms as the live sales report: poll every minute, but only while
  // the tab is visible, and refetch immediately when the user comes back to it.
  const {
    data: orders,
    dataUpdatedAt,
    isFetching,
    error: ordersError,
  } = useRestaurantOrders(restaurant.shopExternalId, dayjs().startOf('day'), {
    refetchInterval: TODAY_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const { data: openingHours } = useRestaurantOpeningHours(restaurant.shopExternalId);

  // Without the orders there is no day to report, and every figure on this card would otherwise
  // settle on a confident zero — "0 orders, no orders yet today" is a claim, and the wrong one.
  const isForbidden = isForbiddenError(ordersError);

  // Cancelled orders are excluded throughout (OrderCancelledTime is the canonical "peruttu" marker,
  // matching OrdersList), so the headline figures and the bars tell the same story. Kept null while
  // loading so the figures show placeholders.
  const activeOrders = useMemo(() => orders?.filter((order) => !order.OrderCancelledTime), [orders]);

  const stats = useMemo(
    () =>
      activeOrders
        ? {
            orderCount: activeOrders.length,
            productCount: countProductsInOrders(activeOrders),
          }
        : null,
    [activeOrders]
  );

  const hourly = useMemo(
    () => getHourlyOrderCounts(activeOrders ?? [], getTodaysOpenHourWindow(restaurant, openingHours)),
    [restaurant, activeOrders, openingHours]
  );

  if (isForbidden) {
    return (
      <DashboardCard title={t('singleRestaurant.today.title')} icon={<InsertChartOutlined fontSize="small" />}>
        <ForbiddenNote />
      </DashboardCard>
    );
  }

  return (
    <TodayCardView
      stats={stats}
      hourly={hourly}
      ordersPath={`/${restaurant.shopExternalId}/orders/`}
      // 0 until the first fetch succeeds, which keeps the cycle bar hidden until there is a cycle.
      refreshedAt={dataUpdatedAt || undefined}
      isFetching={isFetching}
    />
  );
};

/* --------------------------------- preorders ------------------------------- */

// How many of today's waiting preorders are listed under the week strip before the rest collapse
// into a "+N" line, matching how the out-of-stock card handles a long list.
const LISTED_PREORDERS = 3;

// The week strip's bars. The floor is the room the number inside needs — a bar is never shorter than
// its own label — and the ceiling is what a day of PREORDER_BAR_FULL_COUNT products reaches.
//
// The ceiling is set against the day block beneath it: once that block took the date strip's size the
// old 70px left the bars looking stunted, a chart shorter than its own axis labels. Raised by roughly
// what the block gained, so the bar still carries the cell.
const PREORDER_BAR_MIN_HEIGHT = 20;
const PREORDER_BAR_MAX_HEIGHT = 84;
const PREORDER_BAR_FULL_COUNT = 50;

/**
 * Bar height for a day's product count.
 *
 * Absolute, not scaled to the busiest day of the week on show: the point of the bars is spotting a
 * heavy day from across the room, and a relative scale would draw a week peaking at three products
 * exactly like one peaking at three hundred. The cost is that anything past the ceiling is simply
 * full — which is why the number stays printed inside, to say how far past.
 */
const preorderBarHeight = (count: number): number =>
  Math.min(
    PREORDER_BAR_MAX_HEIGHT,
    PREORDER_BAR_MIN_HEIGHT + (count / PREORDER_BAR_FULL_COUNT) * (PREORDER_BAR_MAX_HEIGHT - PREORDER_BAR_MIN_HEIGHT)
  );

// Presentational half of PreordersCard, so the states can be rendered from fixtures (see testStates).
export const PreordersCardView: React.FC<{
  week: PreorderDay[];
  upcoming: UpcomingPreorder[];
  externalId: string;
}> = ({ week, upcoming, externalId }) => {
  const { t } = useTranslation();
  const ordersPath = `/${externalId}/orders/`;
  const today = dayjs().startOf('day');
  // A week with nothing in it gets no bars at all, rather than a hundred pixels of blank chart.
  const hasPreorders = week.some((day) => day.count > 0);

  return (
    <DashboardCard
      title={t('singleRestaurant.preorders.title')}
      icon={<EventOutlined fontSize="small" />}
      to={ordersPath}
    >
      {/* Says what the bars are counting: the number in each one is products, not orders. */}
      {hasPreorders && <CardSubtitle>{t('singleRestaurant.preorders.productsPerDay')}</CardSubtitle>}

      {/* With nothing waiting, the day buttons are the whole card — so they also stop carrying the
          gap meant for the list beneath them. */}
      <WeekRow $standalone={upcoming.length === 0}>
        {week.map((day) => (
          <DayLink
            key={day.date.valueOf()}
            to={`${ordersPath}?date=${day.date.format('YYYY-MM-DD')}`}
            aria-label={`${day.date.format(FinnishDayAndMonthFormat)} · ${day.count}`}
          >
            {hasPreorders && (
              <BarTrack>{day.count > 0 && <Bar $height={preorderBarHeight(day.count)}>{day.count}</Bar>}</BarTrack>
            )}
            <DayBlock $isToday={day.date.isSame(today, 'day')}>
              <Weekday>{day.date.format('ddd')}</Weekday>
              <DayDate>{day.date.format(FinnishDayAndMonthFormat)}</DayDate>
            </DayBlock>
          </DayLink>
        ))}
      </WeekRow>

      {/* Nothing waiting means nothing to say about it: no caption, no placeholder line. An empty
          card that still shows its day buttons is doing its job. */}
      {upcoming.length > 0 && (
        <PreorderList>
          {/* Collapsed by default — the week above is the headline and this is the detail. What does
              not hide with it is the fact that something is overdue: that badge rides on the summary,
              so the one row worth acting on is never folded out of sight. */}
          <PreorderSummary>
            <SummaryChevron>
              <ChevronRight fontSize="small" />
            </SummaryChevron>
            <ChartCaption>{t('singleRestaurant.preorders.upcomingToday')}</ChartCaption>
            <SummaryCount>{upcoming.length}</SummaryCount>
            {upcoming.some((entry) => entry.isLate) && (
              <StateBadge $tone="warning">
                <Dot $tone="warning" />
                {t('singleRestaurant.preorders.late')}
              </StateBadge>
            )}
          </PreorderSummary>
          {upcoming.slice(0, LISTED_PREORDERS).map(({ order, isLate }) => {
            const count = countProductsInOrder(order);

            return (
              <Row key={order.id}>
                <RowLabel>
                  <span>
                    {t('at')} {dayjs(order.preOrderTime).format(FinnishShortTimeFormat)}
                  </span>
                  {isLate && (
                    <StateBadge $tone="warning">
                      <Dot $tone="warning" />
                      {t('singleRestaurant.preorders.late')}
                    </StateBadge>
                  )}
                  {order.deliveryType != null && (
                    <DeliveryChip $type={order.deliveryType}>
                      {t(deliveryTypeMeta(order.deliveryType).labelKey)}
                    </DeliveryChip>
                  )}
                </RowLabel>
                <RowState>
                  {count} {t('singleRestaurant.products', { count })}
                </RowState>
              </Row>
            );
          })}
          {upcoming.length > LISTED_PREORDERS && (
            <MoreLink to={ordersPath}>
              +{upcoming.length - LISTED_PREORDERS} · {t('singleRestaurant.showAll')}
            </MoreLink>
          )}
        </PreorderList>
      )}
    </DashboardCard>
  );
};

// The week ahead as the dashboard's way into the orders view — one button per day carrying that
// day's preorder count — over the few of today's that the kitchen has yet to start.
export const PreordersCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const { t } = useTranslation();
  const { data: preorderCounts, error: countsError } = useRestaurantPreorders(restaurant.shopExternalId);
  const { data: todayOrders } = useRestaurantOrders(restaurant.shopExternalId, dayjs().startOf('day'));

  const week = useMemo(() => getPreorderWeek(preorderCounts), [preorderCounts]);
  const upcoming = useMemo(() => getUpcomingPreorders(todayOrders), [todayOrders]);

  // Without the counts, every day's bar would be absent and the week would read as "no preorders
  // all week" — the one thing a manager must not be told wrongly.
  if (isForbiddenError(countsError)) {
    return (
      <DashboardCard title={t('singleRestaurant.preorders.title')} icon={<EventOutlined fontSize="small" />}>
        <ForbiddenNote />
      </DashboardCard>
    );
  }

  return <PreordersCardView week={week} upcoming={upcoming} externalId={restaurant.shopExternalId} />;
};

/* ----------------------------- payment methods ----------------------------- */

// Only flags problems: renders nothing when a merchant exists and every method is enabled.
export const PaymentMethodsCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const {
    data: merchant,
    error: merchantError,
    isInitialLoading: isMerchantLoading,
  } = useMerchant(restaurant.shopExternalId);
  const hasEcomMerchant = Boolean(merchant?.adyenMerchantData?.merchantId);

  const { data: paymentMethods, isInitialLoading: isPaymentMethodsLoading } = useEcomPaymentMethodSettings(
    restaurant.shopExternalId,
    hasEcomMerchant
  );
  const rows = useMemo(() => getPaymentMethodRows(paymentMethods, merchant ?? undefined), [paymentMethods, merchant]);

  // A 403 would otherwise read as "no merchant, no methods".
  const isForbidden = isForbiddenError(merchantError);
  const allPaymentsActive = hasEcomMerchant && rows.length > 0 && rows.every((row) => row.enabled);

  if (isMerchantLoading || isPaymentMethodsLoading || (!isForbidden && allPaymentsActive)) {
    return null;
  }

  return <PaymentMethodsCardView rows={rows} hasEcomMerchant={hasEcomMerchant} isForbidden={isForbidden} />;
};

export const PaymentMethodsCardView: React.FC<{
  rows: PaymentMethodRow[];
  hasEcomMerchant: boolean;
  isForbidden?: boolean;
}> = ({ rows, hasEcomMerchant, isForbidden }) => {
  const { t } = useTranslation();
  const disabledCount = rows.filter((row) => !row.enabled).length;

  return (
    <DashboardCard title={t('singleRestaurant.payments.title')} icon={<PaymentsOutlined fontSize="small" />}>
      {isForbidden ? (
        <ForbiddenNote />
      ) : rows.length === 0 ? (
        // No methods at all is itself a fault worth shouting about, not a neutral empty state.
        <AlertBanner role="alert">
          <Warning fontSize="small" />
          <span>
            {hasEcomMerchant ? t('singleRestaurant.payments.none') : t('singleRestaurant.payments.noMerchant')}
          </span>
        </AlertBanner>
      ) : (
        <>
          {disabledCount > 0 && (
            <AlertBanner role="alert">
              <Warning fontSize="small" />
              <span>{t('singleRestaurant.payments.disabledWarning', { count: disabledCount })}</span>
            </AlertBanner>
          )}
          <MethodList>
            {rows.map((row) => (
              <MethodRow key={row.key}>
                {row.enabled ? (
                  <CheckCircle color="success" fontSize="small" />
                ) : (
                  <Warning color="error" fontSize="small" />
                )}
                <MethodName $enabled={row.enabled}>{row.label}</MethodName>
              </MethodRow>
            ))}
          </MethodList>
        </>
      )}
    </DashboardCard>
  );
};

/* -------------------------------- out of stock ----------------------------- */

export const OutOfStockCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const { items, isLoading } = useCurrentShopOutOfStockItems(restaurant.shopExternalId);

  return <OutOfStockCardView externalId={restaurant.shopExternalId} items={items} loading={isLoading} />;
};

export const OutOfStockCardView: React.FC<{ externalId: string; items: OutOfStockItem[]; loading?: boolean }> = ({
  externalId,
  items,
  loading,
}) => {
  const { t } = useTranslation();
  const outOfStockPath = `/${externalId}/out-of-stock/`;

  return (
    <DashboardCard
      title={t('nav.outOfStockProducts')}
      icon={<ReportProblemOutlined fontSize="small" />}
      to={outOfStockPath}
    >
      {loading ? (
        <LoadingIndicator height="80px" small />
      ) : items.length === 0 ? (
        // Empty state doubles as a shortcut to add out-of-stock products.
        <AddShortcut to={outOfStockPath}>
          <AddCircleOutline fontSize="small" />
          <div>
            <ShortcutTitle>{t('singleRestaurant.outOfStock.none')}</ShortcutTitle>
            <ShortcutHint>{t('singleRestaurant.outOfStock.add')}</ShortcutHint>
          </div>
        </AddShortcut>
      ) : (
        <>
          <ListHeader>
            <HeaderLabel>{t('singleRestaurant.outOfStock.name')}</HeaderLabel>
            <HeaderLabel>{t('singleRestaurant.outOfStock.returns')}</HeaderLabel>
          </ListHeader>
          <div>
            {items.slice(0, 5).map((item) => (
              <Row key={item.id}>
                <RowLabel>{item.name}</RowLabel>
                <RowState>
                  {item.scheduledUntil ? dayjs(item.scheduledUntil).format(FinnishDayAndMonthFormat) : '–'}
                </RowState>
              </Row>
            ))}
          </div>
          {items.length > 5 && (
            <MoreLink to={outOfStockPath}>
              +{items.length - 5} · {t('singleRestaurant.showAll')}
            </MoreLink>
          )}
        </>
      )}
    </DashboardCard>
  );
};

/* --------------------------- restaurant information ------------------------ */

// The dashboard's home for reference data — the things that identify a restaurant rather than
// describe its day. It sits at the bottom because none of it changes while you watch.
export const RestaurantInfoCard: React.FC<{ restaurant: WebshopShopDto }> = ({ restaurant }) => {
  const { data: conciseMenu } = useConciseProductMenu();
  // Menu scope: concise (suppea) when the shop's base menu is the concise menu.
  const isConcise = conciseMenu ? restaurant.baseMenuId === conciseMenu.productMenuId : undefined;

  return <RestaurantInfoCardView restaurant={restaurant} isConcise={isConcise} />;
};

export const RestaurantInfoCardView: React.FC<{
  restaurant: WebshopShopDto;
  // undefined while the concise-menu lookup is still loading, then true/false.
  isConcise?: boolean;
}> = ({ restaurant, isConcise }) => {
  const { t } = useTranslation();

  return (
    <DashboardCard title={t('singleRestaurant.info.title')} icon={<InfoOutlined fontSize="small" />}>
      {/* First of the rows: it is what the restaurant is called in every other system, so it is the
          one people come to this card to read off. */}
      <Row>
        <RowLabel>{t('singleRestaurant.info.shopId')}</RowLabel>
        <RowState>{restaurant.shopExternalId}</RowState>
      </Row>
      <Row>
        <RowLabel>{t('singleRestaurant.info.address')}</RowLabel>
        <RowState>
          {restaurant.streetAddress}, {restaurant.zip} {restaurant.city}
        </RowState>
      </Row>
      <Row>
        <RowLabel>{t('singleRestaurant.info.menu')}</RowLabel>
        <RowState>
          {isConcise === undefined
            ? '–'
            : isConcise
            ? t('singleRestaurant.menu.concise')
            : t('singleRestaurant.menu.wide')}
        </RowState>
      </Row>
    </DashboardCard>
  );
};

/* ---------------------------------- styles --------------------------------- */

/**
 * Where a two-digit date stops fitting a seventh of the preorder card.
 *
 * Worst just above the full-bleed width: from 376px to 400px the card still pays the page's side
 * gutter as well as its own padding, leaving each of the seven days around 38px of content — and
 * `22.12.` at 15px wants more than that, so the trailing dot spilled past the block. Below 375px the
 * card goes edge to edge and wins the gutter back, which is why this reaches *up* from there rather
 * than down.
 */
const NARROW_DAY_MAX_WIDTH = '400px';

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

// A line of explanation under a card's title, for when the numbers inside need saying what they are.
// Sits in the card body rather than the header, so the header stays a title-and-link row.
const CardSubtitle = styled.div`
  margin-top: -${({ theme }) => theme.spacing(0.5)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

const CardLink = styled(Link)`
  display: flex;
  color: ${({ theme }) => theme.palette.primary.main};
`;

// Header status area: the status pill and menu chip always sit side by side (both on mobile and
// desktop). align-items: stretch makes the chip grow to the pill's height so the two read as a set.
// space-between only bites on mobile, where this row stretches full-width and pushes the menu chip
// to the right edge; on desktop the row is shrink-to-fit so pill and chip stay adjacent.
const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
`;

const HoursChip = styled(Chip)`
  && {
    height: auto;
    border-radius: 999px;

    ${({ theme }) => theme.breakpoints.down('sm')} {
      font-size: ${({ theme }) => theme.fontSizes.xs};
    }
  }
`;

// Two columns at every width, phones included: stacked, each channel cost a row of its own to say
// one line's worth, and comparing them meant scrolling between them. minmax(0, 1fr) (rather than
// 1fr) lets the tracks shrink below their content width, which is what keeps both columns inside a
// ~320px viewport.
const ChannelRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const Channel = styled.div`
  min-width: 0;
  padding: 0 6px;
  border-left: 1px solid ${({ theme }) => theme.palette.divider};

  &:first-child {
    padding-left: 0;
    border-left: none;
  }

  &:last-child {
    padding-right: 0;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: 0 ${({ theme }) => theme.spacing(2)};
  }
`;

// Sweeps left to right once per refetch cycle, so the wait for the next update is visible without
// a countdown or a timestamp to read.
const refreshCycle = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const RefreshTrack = styled.div`
  height: 3px;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  border-radius: 999px;
  background-color: ${({ theme }) => theme.palette.neutral.fill};
  overflow: hidden;
`;

const RefreshFill = styled.span<{ $cycleMs: number; $fetching: boolean }>`
  display: block;
  height: 100%;
  transform-origin: left center;
  background-color: ${CHART_COLORS.brandGreen};
  animation: ${refreshCycle} ${({ $cycleMs }) => $cycleMs}ms linear forwards;
  /* Dimmed while the refetch it just triggered is in flight. */
  opacity: ${({ $fetching }) => ($fetching ? 0.45 : 1)};
  transition: opacity 150ms linear;
`;

// The two headline figures of the today card, side by side above the hourly bars.
const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Stat = styled.div`
  min-width: 0;
`;

// Caption and peak-hour note share a row, wrapping to two lines only when a phone is too narrow
// to fit both.
const CaptionRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-bottom: 4px;
`;

const PeakNote = styled.span`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  white-space: nowrap;
`;

const ChartCaption = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

// The chart fills the card and re-lays out per breakpoint rather than scrolling: a scroll container
// would carry the y-axis off-screen with it and fight the page's own vertical scroll on touch.
// On phones it also bleeds most of the way into the card's padding — 24px of plot width, more than
// every other narrow-screen saving combined — while the text above stays inset.
const ChartBox = styled.div`
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-inline: -12px;
    width: calc(100% + 24px);
  }
`;

// Three columns of three short lines each, sized to survive a 320px phone without stacking: every
// line is one row tall and clipped rather than wrapped, so the card's height never depends on how
// long a channel's name or opening hours happen to be.
const ChannelLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: ${({ theme }) => theme.palette.text.secondary};
  /* The card carries no title of its own, so these labels are its headings: uppercase and tracked. */
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  /* Uppercase and tracking cost roughly a tenth of the line's width, which "NOUTO / PAIKAN PÄÄLLÄ"
     does not have to spare beside its icon on a 320px screen. Given back here. */
  ${({ theme }) => theme.breakpoints.down('sm')} {
    letter-spacing: 0.02em;
  }

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Shown at every width now that the card is two columns rather than three — there is room for the
// mark beside the label, and the mark is the faster of the two to recognise.
const ChannelIcon = styled.span`
  display: flex;
  flex-shrink: 0;
`;

const ChannelValue = styled.div<{ $tone?: StatusTone }>`
  margin-top: 2px;
  font-size: clamp(${({ theme }) => theme.fontSizes.md}, 4.5vw, ${({ theme }) => theme.fontSizes.xl});
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $tone }) => ($tone ? toneColor(theme, $tone) : theme.palette.text.primary)};
`;

const ChannelHours = styled.div<{ $tone?: StatusTone }>`
  margin-top: 1px;
  min-height: 14px;
  color: ${({ theme, $tone }) => ($tone ? toneColor(theme, $tone) : theme.palette.text.secondary)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ $tone }) => ($tone ? 600 : 400)};
  white-space: nowrap;
`;

const StateBadge = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  background-color: ${({ theme, $tone }) => alpha(toneColor(theme, $tone), 0.14)};
`;

/**
 * The week ahead: seven equal cells that shrink with the card rather than scrolling, since a fixed
 * seven never needs the sideways scroll the orders view's own 22-day strip does.
 */
const WeekRow = styled.div<{ $standalone: boolean }>`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: ${({ theme, $standalone }) => ($standalone ? 0 : theme.spacing(2))};
`;

// One cell: a bar for the day's preorder products standing on a small date block.
const DayLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  min-width: 0;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

// Fixed height so every bar is measured off the same baseline — the row only reads as a chart if a
// short bar is short because the day is quiet, not because the cell happened to be smaller.
const BarTrack = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: ${PREORDER_BAR_MAX_HEIGHT}px;
`;

// The number sits at the top of its own bar rather than centred in it: on a tall bar a centred
// figure floats in the middle of nothing, and level with the tip it reads as a label for the height.
// Dark ink rather than white — amber is a light fill, and the value is text, not part of the mark.
const Bar = styled.span<{ $height: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  max-width: 34px;
  height: ${({ $height }) => $height}px;
  padding-top: 3px;
  border-radius: 4px 4px 0 0;
  background-color: ${({ theme }) => theme.palette.highlight.dark};
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  line-height: 1;
`;

/**
 * The date block the bar stands on: the same object as a day button on the orders view's date strip,
 * so arriving there from here is continuous.
 *
 * Shape and lettering are the strip's: 8px corners, uppercase, its two type sizes. It keeps the
 * strip's phone sizes at every width rather than growing at md as the strip does — seven of these
 * share one card's width, where the strip's row can scroll.
 *
 * Today takes the strip's *selected* colours, a solid primary fill with contrast text, rather than
 * the strip's paler "today" ring. This week always begins at today, so it is the day the card is
 * about; on the strip, today is merely one candidate among the days you might pick, and the day you
 * did pick is the one that earns the fill. Same treatment, given to whichever day the view is built
 * around. The ring goes with it — it was there to mark a day the fill did not.
 */
const DayBlock = styled.span<{ $isToday: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  min-width: 0;
  padding: 6px 2px;
  border-radius: 8px;
  background-color: ${({ theme, $isToday }) => ($isToday ? theme.palette.primary.main : theme.palette.neutral.fill)};
  color: ${({ theme, $isToday }) => ($isToday ? theme.palette.primary.contrastText : theme.palette.primary.main)};
  text-transform: uppercase;
  text-align: center;
  transition: background-color 150ms ease, color 150ms ease;
  &:hover {
    background-color: ${({ theme, $isToday }) =>
      $isToday ? theme.palette.primary.dark : theme.palette.neutral.fillHover};
  }
`;

const Weekday = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.8;
  line-height: 1.1;
`;

const DayDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  line-height: 1.1;

  @media (max-width: ${NARROW_DAY_MAX_WIDTH}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

/**
 * The upcoming list, folded away. A native <details> rather than a component: it opens and closes,
 * takes keyboard focus and announces itself to a screen reader without any state to keep in sync.
 */
const PreorderList = styled.details`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const PreorderSummary = styled.summary`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  cursor: pointer;
  user-select: none;

  /* Both spellings: the default disclosure triangle is a ::marker in most engines and a
     ::-webkit-details-marker in older WebKit, and the chevron below replaces it. */
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
`;

const SummaryChevron = styled.span`
  display: flex;
  color: ${({ theme }) => theme.palette.text.secondary};
  transition: transform 150ms ease;

  ${PreorderList}[open] & {
    transform: rotate(90deg);
  }
`;

// The count stays on the summary so a folded list still says how much is behind it.
const SummaryCount = styled.span`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
`;

// The "+N" row that ends a truncated list, carrying on to the page that holds the rest. A link
// rather than a dead count: the number is the reason to go, so it may as well be the way there.
const MoreLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const HeaderLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};

  &:last-child {
    border-bottom: none;
  }
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
`;

// Accent colour + translation key per OrderV2.deliveryType (see shared/Orders/constants): 0 pickup,
// 1 delivery, 2 eat-in, 3 hot stop. Distinct hues rather than status tones, since the type isn't
// a good/bad signal — just a category.
type DeliveryTypeMeta = { color: (theme: Theme) => string; labelKey: string };

const DELIVERY_TYPE_META: Record<number, DeliveryTypeMeta> = {
  0: { color: (theme) => theme.palette.success.dark, labelKey: 'singleRestaurant.deliveryType.pickup' }, // Nouto
  1: { color: (theme) => theme.palette.warning.main, labelKey: 'singleRestaurant.deliveryType.delivery' }, // Kuljetus
  2: { color: (theme) => theme.palette.success.dark, labelKey: 'singleRestaurant.deliveryType.eatin' }, // Paikan päällä
  3: { color: (theme) => theme.palette.neutral.dark, labelKey: 'singleRestaurant.deliveryType.hotStop' }, // Hot Stop
};

const deliveryTypeMeta = (type?: number): DeliveryTypeMeta =>
  (type != null && DELIVERY_TYPE_META[type]) || {
    color: (theme) => theme.palette.neutral.dark,
    labelKey: 'singleRestaurant.deliveryType.unknown',
  };

const DeliveryChip = styled.span<{ $type?: number }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $type }) => deliveryTypeMeta($type).color(theme)};
  background-color: ${({ theme, $type }) => alpha(deliveryTypeMeta($type).color(theme), 0.14)};
`;

const RowState = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const Dot = styled.span<{ $tone: StatusTone }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme, $tone }) => toneColor(theme, $tone)};
`;

const MethodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MethodRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const MethodName = styled.span<{ $enabled: boolean }>`
  color: ${({ theme, $enabled }) => ($enabled ? theme.palette.text.primary : theme.palette.error.main)};
  font-weight: ${({ $enabled }) => ($enabled ? 400 : 600)};
`;

const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => alpha(theme.palette.error.main, 0.4)};
  background-color: ${({ theme }) => alpha(theme.palette.error.main, 0.12)};
  color: ${({ theme }) => theme.palette.error.main};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
`;

const EmptyText = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const AddShortcut = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const ShortcutTitle = styled.div`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
`;

const ShortcutHint = styled.div`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
`;
